import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Layers, GitBranch, Database, CheckCircle2, ArrowRight } from 'lucide-react';

export const Route = createFileRoute('/products/sdd-forge')({
  component: SDDForgePage,
});

const features = [
  { icon: FileText, title: 'Automated SDDs', description: 'Generate comprehensive Software Design Documents from your codebase automatically.' },
  { icon: Layers, title: 'Architecture Diagrams', description: 'Visualize system architecture, component relationships, and data flows.' },
  { icon: GitBranch, title: 'Change Impact Analysis', description: 'Understand how changes propagate through your system before you make them.' },
  { icon: Database, title: 'Data Model Documentation', description: 'Auto-generate ERDs and document database schemas with relationships.' },
];

const sddSections = [
  'System Overview & Architecture',
  'Component Specifications',
  'API Contracts & Interfaces',
  'Data Models & Schema',
  'Security Considerations',
  'Performance Requirements',
  'Deployment Architecture',
  'Integration Points',
];

function SDDForgePage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Layers className="h-10 w-10 text-blue-500" />
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">SDD-Forge</h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Automatically generate and maintain Software Design Documents that stay in sync with your code.
            </p>
          </div>
          <section className="mb-24">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card key={feature.title}>
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-blue-500" />
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
          <section className="mb-24">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Complete SDD Generation</h2>
              <Card className="bg-blue-500/5 border-blue-500/20">
                <CardContent className="py-8">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {sddSections.map((section, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <span>{section}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
          <section className="mb-24">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Keep Documentation Current</h2>
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold mb-2">Continuous Sync</h3>
                  <p className="text-muted-foreground">SDDs automatically update when code changes, eliminating documentation drift.</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold mb-2">Version History</h3>
                  <p className="text-muted-foreground">Track how your architecture evolves over time with versioned documentation.</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold mb-2">Export Formats</h3>
                  <p className="text-muted-foreground">Export to Markdown, PDF, Confluence, or integrate with your existing documentation systems.</p>
                </div>
              </div>
            </div>
          </section>
          <section>
            <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20 max-w-2xl mx-auto">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                  Connect your repository and generate your first Software Design Document in minutes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                      Get Started Free <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="outline">View Pricing</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
